/**
 * Pure logic used by the Grocery Lists page (app/(tabs)/grocery-lists.tsx).
 * Extracted for unit testing without rendering React Native.
 */

import type { GroceryList, GroceryItem } from '@/lib/grocery-list-cache';

export type CheckedItemsBehavior = 'hide' | 'strikethrough';

/**
 * Returns items to display for the active list, ordered by position then unchecked first, then checked.
 * When behavior is 'hide', only unchecked items are returned.
 */
export function getDisplayItems(
  activeList: GroceryList | undefined | null,
  checkedItemsBehavior: CheckedItemsBehavior
): GroceryItem[] {
  if (!activeList) return [];
  const byPosition = [...activeList.items].sort(
    (a, b) => a.position - b.position || a.addedAt - b.addedAt || a.id.localeCompare(b.id)
  );
  const unchecked = byPosition.filter((i) => !i.checked);
  const checked = byPosition.filter((i) => i.checked);
  return checkedItemsBehavior === 'hide' ? unchecked : [...unchecked, ...checked];
}

/**
 * Computes the next active list id when the lists array changes.
 * If lists are empty, returns ''. If current active id is not in the list, returns the first list's id.
 */
export function getNextActiveListId(
  lists: { id: string }[],
  currentActiveId: string
): string {
  if (lists.length === 0) return '';
  const currentExists = lists.some((l) => l.id === currentActiveId);
  if (!currentExists) return lists[0]?.id ?? '';
  return currentActiveId;
}

/**
 * Returns the active list id to use after deleting a list.
 * If the deleted list was active, returns another list's id; otherwise returns current.
 */
export function getActiveListIdAfterDelete(
  deletedListId: string,
  currentActiveId: string,
  lists: { id: string }[]
): string {
  if (currentActiveId !== deletedListId) return currentActiveId;
  const nextList = lists.find((l) => l.id !== deletedListId);
  return nextList?.id ?? '';
}

/**
 * Whether the user can delete a list (must keep at least one).
 */
export function canDeleteList(lists: { id: string }[]): boolean {
  return lists.length > 1;
}

/**
 * Validates new list name for create. Returns valid + trimmed name or invalid + error message.
 */
export function validateNewListName(
  name: string
): { valid: true; trimmed: string } | { valid: false; error: string } {
  const trimmed = name.trim();
  if (!trimmed) {
    return { valid: false, error: 'Please enter a list name' };
  }
  return { valid: true, trimmed };
}
